import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from 'axios';
import {
  Box,
  Card,
  Typography,
  Stack,
  useMediaQuery,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Collapse,
  CircularProgress,
} from "@mui/material";
import fileUploadIcon from "../icon/files_upload.png";
import applicationScoreIcon from "../icon/appn.png";
import financialScoreIcon from "../icon/ruppee.png";
import verificationIcon from "../icon/verifiication.png";
import bureauScoreIcon from "../icon/bureau.png";
import lowRiskIcon from "../icon/overall.png";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";

import finanaceicon from "../icon/financialicon.png";
import crediticon from "../icon/crediticon.png";
import managmenticon from "../icon/managmenticon.png";
import { API_BASE } from '../api';

// Helper function to ensure safe rendering of values
const safeValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    return 'N/A';
  }
  return String(value || 'N/A');
};

// Helper: badge icon by progress
const ProgressBadge = ({ percent = 0 }) => {
  const GREY = '#C7C7C7';
  const BLUE = '#034A7F';
  const GREEN = '#5ABA85';

  let icon = <CancelIcon sx={{ fontSize: 18, color: GREY }} />;
  let bg = 'transparent';
  let border = GREY;

  if (percent >= 100) {
    icon = <CheckCircleOutlineIcon sx={{ fontSize: 18, color: GREEN }} />;
  } else if (percent > 0) {
    icon = <HourglassBottomIcon sx={{ fontSize: 18, color: BLUE }} />;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', pr: 1 }}>
      <Box sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bg,
        border: `1px solid ${border}`,
      }}>
        {icon}
      </Box>
    </Box>
  );
};

// Tab 0: Application Details Content
const ApplicationDetailsTab = ({ isSmallScreen, verificationCount = '0/0' }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const applicationId = queryParams.get("id");

  const [overallScore, setOverallScore] = useState(null);
  const [proprietorScore, setProprietorScore] = useState(null);  
  const [coApplicantScore, setCoApplicantScore] = useState(null);
  const [companyScore, setCompanyScore] = useState(null);
  const [docProgress, setDocProgress] = useState("0/0");
  const [verProgress, setVerProgress] = useState("0/0");
  const [totalFiles, setTotalFiles] = useState(0);
  const [verifiedFiles, setVerifiedFiles] = useState(new Set());

  //  Calculate verification percentage
  const getVerificationPercent = () => {
    try {
      const [verified, total] = verProgress.split('/').map(Number);
      if (total > 0) {
        return Math.round((verified / total) * 100);
      }
    } catch (e) {}
    return 0;
  };

  // Handle file verification with tracking of verified files
  // Line ~45 - REPLACE ENTIRE FUNCTION
const handlePageVerification = async (documentType, role, pageNumber) => {
    if (!applicationId) {
        console.warn('[VERIFY] Missing applicationId');
        return { success: false, error: 'Missing applicationId' };
    }

    // Generate consistent pageId format
    // Example: "ITR_PR_page_1", "PAN_CO1_page_1", "CIBIL_PR_page_2"
    const pageId = `${documentType}_${role || 'MAIN'}_page_${pageNumber}`;
    
    console.log(` [VERIFY] Attempting to verify page: ${pageId}`);
    
    try {
        const response = await fetch(
            `${API_BASE}/applications/${applicationId}/verification/increment`, 
            {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ pageId })  // ← Changed from fileId to pageId
            }
        );

        const result = await response.json();
        console.log(' [VERIFY] Backend response:', result);
        
        if (result.success) {
            // Update verification progress display
            setVerProgress(result.display);
            
            if (result.isNewVerification) {
                console.log(` NEW verification! Progress: ${result.display}`);
            } else {
                console.log(`Page already verified. Progress: ${result.display}`);
            }
        }
        
        return result;
    } catch (error) {
        console.error(' [VERIFY] Request failed:', error);
        return { 
            success: false, 
            error: error.message,
            isNewVerification: false
        };
    }
};

  

  // Add this useEffect to sync verification status when component mounts
  // useEffect(() => {
  //   const syncVerificationStatus = async () => {
  //     try {
  //       const response = await fetch(
  //         `${API_BASE}/applications/${applicationId}/verification/status`
  //       );
  //       const result = await response.json();
        
  //       if (result.success) {
  //         // Update verification progress
  //         setVerProgress(`${result.verifiedCount}/${result.totalFiles}`);
          
  //         // Update verified files in state
  //         if (Array.isArray(result.verifiedFiles)) {
  //           setVerifiedFiles(new Set(result.verifiedFiles));
  //         }
  //       }
  //     } catch (error) {
  //       console.error('Failed to sync verification status:', error);
  //     }
  //   };
    
  //   if (applicationId) {
  //     syncVerificationStatus();
  //   }
  // }, [applicationId]);

 useEffect(() => {
  if (!applicationId) return;

  const fetchScores = async () => {
    try {
      console.log(' [FETCH] Starting fetch for applicationId:', applicationId);
      
      // OPTIMIZED: Fetch application details directly instead of fetching entire list
      // This eliminates the need to fetch ALL applications just to find one
      const resApp = await fetch(`${API_BASE}/applications/${applicationId}/details`);
        if (resApp.ok) {
          const jsonApp = await resApp.json();
          if (jsonApp.success) {
            setOverallScore(jsonApp.data?.scores?.application?.score ?? "N/A");
            
            // Get file progress display
            const fp = jsonApp.data?.fileProgress?.display;
            if (typeof fp === 'string') setDocProgress(fp);
            
            // Get verification progress data - use backend value directly
            const verificationData = jsonApp.data?.verificationProgress;
            if (typeof verificationData === 'string') {
              setVerProgress(verificationData);  // Use backend value as-is
            } else {
              setVerProgress(`0/21`);  // Default value
            }
            
            // Get totalFiles from application details
            const total = jsonApp.data?.totalJSONFiles || 21;
            setTotalFiles(total);
            console.log(' Total Files set to:', total);
          }
        }

      // 2️ Proprietor & Co-Applicant Scores
      const resInd = await fetch(`${API_BASE}/individuals/${applicationId}/scores`);
      if (resInd.ok) {
        const jsonInd = await resInd.json();
        if (jsonInd.success) {
          setProprietorScore(jsonInd.data?.proprietor?.scores?.profile ?? "N/A");
          setCoApplicantScore(jsonInd.data?.coApplicant?.scores?.profile ?? "N/A");
        }
      }

      // 3️ Company Scores
      const resCompany = await fetch(`${API_BASE}/companies/${applicationId}/scores`);
      if (resCompany.ok) {
        const jsonCompany = await resCompany.json();
        if (jsonCompany.success) {
          const rawScore = jsonCompany.data?.profileScore;

          console.log("Raw company score:", rawScore);

          setCompanyScore(rawScore ?? "N/A");
      }
    }
      console.log('[FETCH] All data fetched successfully');
    } catch (err) {
      console.error("Score fetch error:", err);
    }
  };

  fetchScores();
}, [applicationId, totalFiles]);

  return (
  <Box>
  <Stack spacing={2} alignItems="stretch" justifyContent="center" sx={{ height: "100%", width: "100%" }}>
    <Card
      sx={{
        borderRadius: "1rem",
        boxShadow: 3,
        width: "100%",
        p: 2,
        px: 3,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .outer-text": {
            color: "white !important",
            transition: "color 0.4s ease-in-out",
          },
          "& svg": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .progress-bar-bg": {
            backgroundColor: "#C7C7C7 !important",
          },
          "& .progress-bar-fill": {
            backgroundColor: "#5ABA85 !important",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: 0,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
        "&:hover .score-divider": {
          borderColor: "white !important",
        },
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ width: "100%", mt: 2 }}>
          <Collapse in={true} timeout="auto" unmountOnExit>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "stretch",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "1rem",
                }}
              >
                {[
                  { icon: applicationScoreIcon, title: "Overall", value: safeValue(overallScore) },
                  { icon: fileUploadIcon, title: "Proprietor", value: safeValue(proprietorScore) },
                  { icon: verificationIcon, title: "Co-applicant", value: safeValue(coApplicantScore) },
                  { icon: lowRiskIcon, title: "Company", value: safeValue(companyScore) },
                ].map((item, index) => {
                  const roundedValue =
                    item.value != null && !isNaN(parseFloat(item.value))
                      ? Number(parseFloat(item.value).toFixed(1))
                      : null;
                  
                  const backgroundColor = roundedValue != null && roundedValue < 3.5 ? "#ff7769" : "#fff";
                  const textColor = roundedValue != null && roundedValue < 3.5 ? "#fff" : "#034A7F";
                  const valueColor = roundedValue != null && roundedValue < 3.5 ? "#fff" : "#C7C7C7";
                  const imageFilter =
                    roundedValue != null && roundedValue < 3.5
                      ? "brightness(0) invert(1)"
                      : "none";

                  return (
                    <React.Fragment key={index}>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "0.75rem",
                          boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
                          p: { xs: "1rem", sm: "0.8rem" },
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          backgroundColor: backgroundColor,
                          flex: 1,
                          minHeight: { xs: "4rem", sm: "auto" },
                          position: "relative",
                          zIndex: 2,
                          transition: "all 0.4s ease-in-out",
                        }}
                      >
                        <img 
                          src={item.icon} 
                          alt={item.title} 
                          style={{ 
                            width: "2.2rem", 
                            height: "2.2rem", 
                            filter: imageFilter 
                          }} 
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ 
                            color: textColor, 
                            fontWeight: 600, 
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            lineHeight: 1.2
                          }}>
                            {item.title}
                          </Typography>
                          <Typography sx={{
                            color: valueColor,
                            fontWeight: 600,
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            lineHeight: 1.2
                          }}>
                            {roundedValue != null ? roundedValue : "N/A"}
                          </Typography>
                        </Box>
                      </Box>

                      {index === 0 && (
                        <Divider
                          className="score-divider"
                          orientation="vertical"
                          flexItem
                          sx={{
                            display: { xs: "none", sm: "block" },
                            mx: "0.5rem",
                            borderColor: "rgba(150,150,150,0.4)",
                            zIndex: 5,
                            position: "relative",
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Stack>
    </Card>
  </Stack>

  {/* Bottom Row - Two Cards Side by Side */}
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={2}
    mt={2}
    alignItems="stretch"
  >
    {/* Documents Card */}
    <Card
      sx={{
        borderRadius: "1rem",
        p: 2,
        boxShadow: 3,
        width: { xs: "100%", sm: "50%" },
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .outer-text": {
            color: "white !important",
            transition: "color 0.4s ease-in-out",
          },
          "& svg": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .progress-bar-bg": {
            backgroundColor: "#C7C7C7 !important",
          },
          "& .progress-bar-fill": {
            backgroundColor: "#5ABA85 !important",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: 0,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
        "&:hover .score-divider": {
          borderColor: "white !important",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Stack spacing={1}>
          {/* Header with Progress */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ width: "100%" }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              className="outer-text"
              sx={{ color: "#034A7F", fontSize: { xs: "0.95rem", sm: "1rem" } }}
            >
              Onboarding Status
            </Typography>
            <Box sx={{ minWidth: { xs: "100%", sm: "12.5rem" } }}>
              <ProgressBadge percent={getVerificationPercent()} />
            </Box>
          </Stack>

          <Box sx={{ width: "100%", mt: 2 }}>
            <Collapse in={true} timeout="auto" unmountOnExit>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "stretch",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "1rem",
                  }}
                >
                  {[
                    { icon: fileUploadIcon, title: "Documents", value: docProgress },
                    { icon: verificationIcon, title: "Verification", value: verProgress },
                  ].map((item, index) => {
                    const numericValue = Number(String(item.value).split("/")[0]);
                    const isLow =
                      item.title === "Verification" && !isNaN(numericValue) && numericValue < 21;

                    const bgColor = isLow ? "#FF7769" : "#fff";
                    const titleColor = isLow ? "#fff" : "#034A7F";
                    const textColor = isLow ? "#fff" : "#C7C7C7";
                    const iconFilter = isLow ? "brightness(0) invert(1)" : "none";

                    return (
                      <Box
                        key={index}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "0.75rem",
                          boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
                          p: { xs: "1rem", sm: "0.8rem" },
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          backgroundColor: bgColor,
                          flex: 1,
                          minHeight: { xs: "4rem", sm: "auto" },
                          position: "relative",
                          zIndex: 2,
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            backgroundColor: bgColor,
                            transform: "none",
                          },
                        }}
                      >
                        <img
                          src={item.icon}
                          alt={item.title}
                          style={{
                            width: "2.2rem",
                            height: "2.2rem",
                            filter: iconFilter,
                          }}
                        />

                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              color: titleColor,
                              fontWeight: 600,
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              lineHeight: 1.2
                            }}
                          >
                            {item.title}
                          </Typography>

                          <Typography
                            sx={{
                              color: textColor,
                              fontWeight: 600,
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              lineHeight: 1.2
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </Box>
    </Card>

    {/* Application Summary Card */}
    <Card
      sx={{
        borderRadius: "1rem",
        p: 2,
        boxShadow: 3,
        width: { xs: "100%", sm: "50%" },
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .MuiTypography-root": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .hoverText": {
            color: "#034A7F",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: -1,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        mb={1}
        sx={{ 
          color: "#034A7F",
          fontSize: { xs: "0.95rem", sm: "1rem" }
        }}
      >
        Profile Summary
      </Typography>

      <Box
        sx={{
          border: "1px solid #C7C7C7",
          borderRadius: "0.5rem",
          p: 1.5,
          backgroundColor: "#fff",
          minHeight: { xs: "6rem", sm: "7.5rem" },
        }}
      >
        <Typography
          variant="body2"
          className="hoverText"
          sx={{
            color: "#C7C7C7",
            transition: "color 0.4s ease-in-out",
            fontSize: { xs: "0.9rem", sm: "1rem" }
          }}
        >
          Coming Soon...
        </Typography>
      </Box>
    </Card>
  </Stack>
</Box>
  );
};

// Tab 1: Financial Details Content
const FinancialDetailsTab = ({ isSmallScreen, verificationCount = '0/0' }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const applicationId = queryParams.get("id");

  const [overallScore, setOverallScore] = useState(null);
  const [proprietorScore, setProprietorScore] = useState(null);  
  const [coApplicantScore, setCoApplicantScore] = useState(null);
  const [companyScore, setCompanyScore] = useState(null);
  const [docProgress, setDocProgress] = useState("0/0");
  const [verProgress, setVerProgress] = useState("0/0");
  const [totalFiles, setTotalFiles] = useState(0);

  useEffect(() => {
    if (!applicationId) return;

    const fetchScores = async () => {
      try {
        // OPTIMIZED: Fetch application details directly instead of entire list
        const resApp = await fetch(`${API_BASE}/applications/${applicationId}/details`);
        if (resApp.ok) {
          const jsonApp = await resApp.json();
          if (jsonApp.success) {
            setOverallScore(jsonApp.data?.scores?.financial?.score ?? "N/A");
            
            // Get file progress display
            const fp = jsonApp.data?.fileProgress?.display;
            if (typeof fp === 'string') setDocProgress(fp);
            
            // Get verification progress data - use backend value directly
            const verificationData = jsonApp.data?.verificationProgress;
            if (typeof verificationData === 'string') {
              setVerProgress(verificationData);  // Use backend value as-is
            } else {
              setVerProgress(`0/21`);  // Default value
            }
            
            // Get totalFiles from application details
            const total = jsonApp.data?.totalJSONFiles || 21;
            setTotalFiles(total);
          }
        }

        // Proprietor & Co-Applicant Profile Scores
        const resInd = await fetch(`${API_BASE}/individuals/${applicationId}/scores`);
        if (resInd.ok) {
          const jsonInd = await resInd.json();
          if (jsonInd.success) {
           setProprietorScore(jsonInd.data?.proprietor?.scores?.financial ?? "N/A");
           setCoApplicantScore(jsonInd.data?.coApplicant?.scores?.financial ?? "N/A");
          }
        }

        // Company Scores
        const resCompany = await fetch(`${API_BASE}/companies/${applicationId}/scores`);
        if (resCompany.ok) {
          const jsonCompany = await resCompany.json();
          if (jsonCompany.success) {
            setCompanyScore(jsonCompany.data?.financialScore ?? "N/A");
          }
        }
      } catch (err) {
        console.error("Score fetch error:", err);
      }
    };

    fetchScores();
  }, [applicationId, totalFiles]);

 return (
  <Box>
    <Stack
      spacing={2}
      alignItems="stretch"
      justifyContent="center"
      sx={{ height: "100%", width: "100%" }}
    >
      <Card
        sx={{
          borderRadius: "1rem",
          boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
          width: "100%",
          p: { xs: "1rem", sm: "1.25rem" },
          px: { xs: "1.25rem", sm: "1.75rem" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.4s ease-in-out",
          zIndex: 0,
          cursor: "pointer",
          "&:hover": {
            color: "white",
            "& .outer-text": { color: "white !important" },
            "&::after": { transform: "scaleY(1)" }
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#034A7F",
            zIndex: 0,
            transform: "scaleY(0)",
            transformOrigin: "bottom",
            transition: "transform 0.4s ease-in-out"
          }
        }}
      >
        <Stack spacing={2}>
          <Collapse in timeout="auto" unmountOnExit>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "1rem"
              }}
            >
              {[
                { icon: applicationScoreIcon, title: "Overall", value: safeValue(overallScore) },
                { icon: fileUploadIcon, title: "Proprietor", value: safeValue(proprietorScore) },
                { icon: verificationIcon, title: "Co-applicant", value: safeValue(coApplicantScore) },
                { icon: lowRiskIcon, title: "Company", value: safeValue(companyScore) }
              ].map((item, index) => {
                const roundedValue =
                  item.value != null && !isNaN(parseFloat(item.value))
                    ? Number(parseFloat(item.value).toFixed(1))
                    : null;

                const isLow = roundedValue != null && roundedValue < 3.5;

                return (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "0.75rem",
                      boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
                      p: { xs: "1rem", sm: "0.8rem" },
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      backgroundColor: isLow ? "#FF7769" : "#fff",
                      flex: 1,
                      minHeight: { xs: "4rem", sm: "auto" },
                      position: "relative",
                      zIndex: 2,
                      transition: "all 0.3s ease-in-out"
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.title}
                      style={{
                        width: "2.2rem",
                        height: "2.2rem",
                        filter: isLow ? "brightness(0) invert(1)" : "none"
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: isLow ? "#fff" : "#034A7F",
                          fontWeight: 600,
                          fontSize: { xs: "0.9rem", sm: "1rem" }
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: isLow ? "#fff" : "#C7C7C7",
                          fontWeight: 600,
                          fontSize: { xs: "0.9rem", sm: "1rem" }
                        }}
                      >
                        {roundedValue != null ? roundedValue : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Stack>
      </Card>
    </Stack>

    {/* Bottom Row */}
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      mt={2}
      alignItems="stretch"
    >
      <Card
         sx={{
        borderRadius: "1rem",
        p: 2,
        boxShadow: 3,
        width: { xs: "100%", sm: "50%" },
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .outer-text": {
            color: "white !important",
            transition: "color 0.4s ease-in-out",
          },
          "& svg": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .progress-bar-bg": {
            backgroundColor: "#C7C7C7 !important",
          },
          "& .progress-bar-fill": {
            backgroundColor: "#5ABA85 !important",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: 0,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
        "&:hover .score-divider": {
          borderColor: "white !important",
        },
      }}
      >
        <Stack spacing={2}>
          <Stack
          direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ width: "100%" }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                color: "#034A7F",
                fontSize: { xs: "0.95rem", sm: "1rem" }
              }}
            >
              Onboarding Status
            </Typography>
 <Box sx={{ minWidth: { xs: "100%", sm: "12.5rem" } }}>
            <ProgressBadge
              percent={
                (() => {
                  const [verified, total] = verProgress.split("/").map(Number);
                  return total > 0 ? Math.round((verified / total) * 100) : 0;
                })()
              }
            />
            </Box>
          </Stack>

          <Box
            sx={{
              display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "stretch",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "1rem",
            }}
          >
            {[
              { icon: fileUploadIcon, title: "Documents", value: docProgress },
              { icon: verificationIcon, title: "Verification", value: verProgress }
            ].map((item, index) => {
              const numericValue = Number(String(item.value).split("/")[0]);
              const isLow = item.title === "Verification" && numericValue < 21;

              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "0.75rem",
                    boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
                    p: { xs: "1rem", sm: "0.8rem" },
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    backgroundColor: isLow ? "#FF7769" : "#fff",
                    flex: 1,
                    minHeight: { xs: "4rem", sm: "auto" },
                    transition: "all 0.3s ease-in-out"
                  }}
                >
                  <img
                    src={item.icon}
                    alt={item.title}
                    style={{
                      width: "2.2rem",
                      height: "2.2rem",
                      filter: isLow ? "brightness(0) invert(1)" : "none"
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        color: isLow ? "#fff" : "#034A7F",
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", sm: "1rem" }
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: isLow ? "#fff" : "#C7C7C7",
                        fontWeight: 600,
                        fontSize: { xs: "0.9rem", sm: "1rem" }
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Stack>
      </Card>

      <Card
        sx={{
          borderRadius: "1rem",
          p: { xs: "1rem", sm: "1.25rem" },
          boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
          width: { xs: "100%", sm: "50%" }
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          mb={2}
          sx={{
            color: "#034A7F",
            fontSize: { xs: "0.95rem", sm: "1rem" }
          }}
        >
          Financial Summary
        </Typography>

        <Box
          sx={{
            border: "1px solid #C7C7C7",
            borderRadius: "0.5rem",
            p: "1rem",
            backgroundColor: "#fff",
            minHeight: { xs: "6rem", sm: "7.5rem" }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#C7C7C7",
              fontSize: { xs: "0.9rem", sm: "1rem" }
            }}
          >
            Coming Soon...
          </Typography>
        </Box>
      </Card>
    </Stack>
  </Box>
);
};

// Tab 2: Bureau Details Content
const BureauDetailsTab = ({ isSmallScreen, verificationCount = '0/0' }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const applicationId = queryParams.get("id");

  const [overallScore, setOverallScore] = useState(null);
  const [proprietorScore, setProprietorScore] = useState(null);
  const [coApplicantScore, setCoApplicantScore] = useState(null);
  const [companyScore, setCompanyScore] = useState(null);
  const [docProgress, setDocProgress] = useState("0/0");
  const [verProgress, setVerProgress] = useState("0/0");
  const [totalFiles, setTotalFiles] = useState(0);

  useEffect(() => {
    if (!applicationId) return;

    const fetchScores = async () => {
      try {
        // First, fetch the application details to get totalJSONFiles
        // OPTIMIZED: Fetch application details directly
        const resApp = await fetch(`${API_BASE}/applications/${applicationId}/details`);
        if (resApp.ok) {
          const jsonApp = await resApp.json();
          if (jsonApp.success) {
            setOverallScore(jsonApp.data?.scores?.bureau?.score ?? "N/A");
            
            // Get file progress display
            const fp = jsonApp.data?.fileProgress?.display;
            if (typeof fp === 'string') setDocProgress(fp);
            
            // Get verification progress data - use backend value directly
            const verificationData = jsonApp.data?.verificationProgress;
            if (typeof verificationData === 'string') {
              setVerProgress(verificationData);  // Use backend value as-is
            } else {
              setVerProgress(`0/21`);  // Default value
            }
            
            // Get totalFiles from application details
            const total = jsonApp.data?.totalJSONFiles || 21;
            setTotalFiles(total);
          }
        }

        // Proprietor & Co-Applicant Profile Scores
        const resInd = await fetch(`${API_BASE}/individuals/${applicationId}/scores`);
        if (resInd.ok) {
          const jsonInd = await resInd.json();
          if (jsonInd.success) {
            setProprietorScore(jsonInd.data?.proprietor?.scores?.bureau ?? "N/A");
            setCoApplicantScore(jsonInd.data?.coApplicant?.scores?.bureau ?? "N/A");
          }
        }

        //  Company Scores
        const resCompany = await fetch(`${API_BASE}/companies/${applicationId}/scores`);
        if (resCompany.ok) {
          const jsonCompany = await resCompany.json();
          if (jsonCompany.success) {
            setCompanyScore(jsonCompany.data?.bureauScore ?? "N/A");
          }
        }
      } catch (err) {
        console.error("Score fetch error:", err);
      }
    };

    fetchScores();
  }, [applicationId, totalFiles]);

  return (
    <Box>
      {/* Top Row - Two Cards */}
      <Stack spacing={2} alignItems="stretch" justifyContent="center" sx={{ height: "100%", width: "100%" }}>
        <Card
          sx={{
            borderRadius: "1rem",
            boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
            width: "100%",
            p: { xs: "1rem", sm: "1.25rem" },
        px: { xs: "1.25rem", sm: "1.75rem" }, 
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            transition: "color 0.4s ease-in-out",
            zIndex: 0,
            cursor: "pointer",
            "&:hover": {
              color: "white",
              "& .outer-text": {
                color: "white !important",
                transition: "color 0.4s ease-in-out",
              },
              "& svg": {
                color: "white",
                transition: "color 0.4s ease-in-out",
              },
              "& .progress-bar-bg": {
                backgroundColor: "#C7C7C7 !important",
              },
              "& .progress-bar-fill": {
                backgroundColor: "#5ABA85 !important",
              },
              "&::after": {
                transform: "scaleY(1)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#034A7F",
              zIndex: 0,
              transform: "scaleY(0)",
              transformOrigin: "bottom",
              transition: "transform 0.4s ease-in-out",
            },
            "&:hover .score-divider": {
              borderColor: "white !important",
            },
          }}
        >
          <Stack spacing={1}>
            <Box sx={{ width: "100%", mt: 2 }}>
              <Collapse in={true} timeout="auto" unmountOnExit>
                 <Box>
                <Box
                  sx={{
                   display: "flex",
    flexDirection: { xs: "column", sm: "row" }, 
    alignItems: "stretch",
    justifyContent: "space-between",
    width: "100%",
    gap: "1rem",
                  }}
                >
                 {[
                  { icon: applicationScoreIcon, title: "Overall", value: safeValue(overallScore) },
                  { icon: fileUploadIcon, title: "Proprietor", value: safeValue(proprietorScore) },
                  { icon: verificationIcon, title: "Co-applicant", value: safeValue(coApplicantScore) },
                  { icon: lowRiskIcon, title: "Company", value: safeValue(companyScore) },
                ].map((item, index) => {
                  // const roundedValue = item.value != null ? Math.round(item.value) : null;
                  // const roundedValue = item.value != null ? Number(item.value.toFixed(1)) : null;
                  const roundedValue =
                              item.value != null && !isNaN(parseFloat(item.value))
                                ? Number(parseFloat(item.value).toFixed(1))
                                : null;
                  
                  const backgroundColor = roundedValue != null && roundedValue < 3.5 ? "#ff7769" : "#fff";
                  const textColor = roundedValue != null && roundedValue < 3.5 ? "#fff" : "#034A7F";
                  const valueColor = roundedValue != null && roundedValue < 3.5 ? "#fff" : "#C7C7C7";
                  const imageFilter =
                                    roundedValue != null && roundedValue < 3.5
                                      ? "brightness(0) invert(1)"   //  makes icon white
                                      : "none";

                  


                  // console.log(`Before Round: ${item.value}, After Round: ${roundedValue}`);
                                 
                  return (
                    <React.Fragment key={index}>
                      <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                               borderRadius: "0.75rem", 
                             boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)", 
                              // borderTop: `6px solid ${topBorderColor}`, // dynamic top border
 
                              // borderTopLeftRadius: "12px",  //  Ensure top corners are rounded
                              // borderTopRightRadius: "12px", //  Ensure top corners are rounded
                             p: { xs: "1rem", sm: "0.8rem" },
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              backgroundColor: backgroundColor, // always white
                              flex: 1,
    minHeight: { xs: "4rem", sm: "auto" }, // added
    position: "relative",
    zIndex: 2,
    transition: "all 0.3s ease-in-out",
                            }}
                          >
                        <img src={item.icon} alt={item.title} style={{ width:"2.2rem", height: "2.2rem", filter: imageFilter }} />
                        <Box>
                          <Typography sx={{ color: textColor,
    fontWeight: 600,
    fontSize: { xs: "0.9rem", sm: "1rem" },}}>
                            {item.title}
                          </Typography>
                          <Typography sx={{ color: textColor,
    fontWeight: 600,
    fontSize: { xs: "0.9rem", sm: "1rem" },}}>
                            {roundedValue != null ? roundedValue : "N/A"}
                          
                          </Typography>
 
                        </Box>
                      </Box>
 
                      {index === 0 && (
                        <Divider
                          className="score-divider"
                          orientation="vertical"
                          flexItem
                          sx={{
                            mx: "0.5rem",
                            borderColor: "rgba(150,150,150,0.4)",
                            zIndex: 5,
                            position: "relative",
                          }}
                        />
                      )}
                    </React.Fragment>
                    // <React.Fragment key={index}>
                    //   <Box
                    //     sx={{
                    //       border: "1px solid #e0e0e0",
                    //       borderRadius: "12px",
                    //       boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                    //       p: "1rem",
                    //       display: "flex",
                    //       alignItems: "center",
                    //       gap: "0.75rem",
                    //       backgroundColor: backgroundColor,
                    //       flex: 1,
                    //       position: "relative",
                    //       zIndex: 2,
                    //       transition: "all 0.4s ease-in-out",
                    //       "&:hover": {
                    //         backgroundColor: backgroundColor,
                    //         borderColor: "#e0e0e0",
                    //         boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                    //         transform: "none",
                    //       },
                    //     }}
                    //   >
                    //     <img src={item.icon} alt={item.title} style={{ width: 35, height: 35 }} />
                    //     <Box>
                    //       <Typography sx={{ color: textColor, fontWeight: 600, fontSize: "1rem" }}>
                    //         {item.title}
                    //       </Typography>
                    //       <Typography sx={{color:valueColor,fontWeight: 600,fontSize: "1rem",}}>
                    //         {roundedValue != null ? roundedValue : "N/A"}
                          
                    //       </Typography>
 
                    //     </Box>
                    //   </Box>
 
                    //   {index === 0 && (
                    //     <Divider
                    //       className="score-divider"
                    //       orientation="vertical"
                    //       flexItem
                    //       sx={{
                    //         mx: "0.5rem",
                    //         borderColor: "rgba(150,150,150,0.4)",
                    //         zIndex: 5,
                    //         position: "relative",
                    //       }}
                    //     />
                    //   )}
                    // </React.Fragment>
                  );
                })}
 
                </Box>
              </Box>
 
              </Collapse>
            </Box>
          </Stack>
        </Card>
      </Stack>

      {/* Bottom Row - Two Cards Side by Side */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mt={2}
        alignItems="stretch"
      >
        {/* Documents Card */}
        <Card
         sx={{
        borderRadius: "1rem",
        p: 2,
        boxShadow: 3,
        width: { xs: "100%", sm: "50%" },
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .outer-text": {
            color: "white !important",
            transition: "color 0.4s ease-in-out",
          },
          "& svg": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .progress-bar-bg": {
            backgroundColor: "#C7C7C7 !important",
          },
          "& .progress-bar-fill": {
            backgroundColor: "#5ABA85 !important",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: 0,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
        "&:hover .score-divider": {
          borderColor: "white !important",
        },
      }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack spacing={1}>
              {/* Header with Progress */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ width: "100%" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="outer-text"
               sx={{
                color: "#034A7F",
                fontSize: { xs: "0.95rem", sm: "1rem" }
              }}
                >
                  Onboarding Status
                </Typography>
                <Box sx={{ minWidth: { xs: "100%", sm: "12.5rem" } }}>
                  <ProgressBadge percent={50} />
                </Box>
              </Stack>

              <Box sx={{ width: "100%", mt: 2 }}>
                <Collapse in={true} timeout="auto" unmountOnExit>
                  <Box>
                    <Box
                      sx={{
                         display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "stretch",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "1rem",
                      }}
                    >
                      {[
                        { icon: fileUploadIcon, title: "Documents", value: docProgress },
                        { 
                          icon: verificationIcon, 
                          title: `Verification`,
                          value: verProgress 
                        },
                      ].map((item, index) => (
                        <React.Fragment key={index}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: "0.75rem",
                              boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
                              p: { xs: "1rem", sm: "0.8rem" },
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              backgroundColor: "#fff",
                              flex: 1,
                               minHeight: { xs: "4rem", sm: "auto" },
                              position: "relative",
                              zIndex: 2,
                              transition: "all 0.4s ease-in-out",
                              "&:hover": {
                                backgroundColor: "#fff",
                                borderColor: "#e0e0e0",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                                transform: "none",
                              },
                            }}
                          >
                            <img
                              src={item.icon}
                              alt={item.title}
                           style={{ width: "2.2rem", height: "2.2rem" }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  color: "#034A7F",
                                  fontWeight: 600,
                                 fontSize: { xs: "0.9rem", sm: "1rem" }
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#C7C7C7",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                }}
                              >
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </Stack>
          </Box>
        </Card>

        {/* Application Summary Card */}
        <Card
          sx={{
            borderRadius: "16px",
            p: 2,
            boxShadow: 3,
            width: isSmallScreen ? "100%" : "50%",
            position: "relative",
            overflow: "hidden",
            transition: "color 0.4s ease-in-out",
            zIndex: 0,
            cursor: "pointer",
            "&:hover": {
              color: "white",
              "& .MuiTypography-root": {
                color: "white",
                transition: "color 0.4s ease-in-out",
              },
              "& .hoverText": {
                color: "#034A7F",
              },
              "&::after": {
                transform: "scaleY(1)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#034A7F",
              zIndex: -1,
              transform: "scaleY(0)",
              transformOrigin: "bottom",
              transition: "transform 0.4s ease-in-out",
            },
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            mb={1}
            sx={{ color: "#034A7F" }}
          >
            Bureau Summary
          </Typography>

          <Box
            sx={{
              border: "1px solid #C7C7C7",
              borderRadius: "8px",
              p: 1.5,
              backgroundColor: "#fff",
              minHeight: 120,
            }}
          >
            <Typography
              variant="body2"
              className="hoverText"
              sx={{
                color: "#C7C7C7",
                transition: "color 0.4s ease-in-out",
              }}
            >
              Coming Soon...
            </Typography>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
};

// Tab 3: Scorecard Details Content
const ScorecardDetailsTab = ({ isSmallScreen, verificationCount = '0/0' }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const applicationId = queryParams.get("id");

  const [overallScore, setOverallScore] = useState(null);
  const [fScore, setfScore] = useState(null);
  const [bScore, setbScore] = useState(null);
  const [pScore, setpScore] = useState(null);
  const [docProgress, setDocProgress] = useState("0/0");
  const [verProgress, setVerProgress] = useState("0/0");
  const [totalFiles, setTotalFiles] = useState(0);

  useEffect(() => {
    if (!applicationId) return;

    const fetchScores = async () => {
      try {
        // OPTIMIZED: Fetch application details directly
        const resApp = await fetch(`${API_BASE}/applications/${applicationId}/details`);
        if (resApp.ok) {
          const jsonApp = await resApp.json();
          if (jsonApp.success) {
            setOverallScore(jsonApp.data?.scores?.overall?.score ?? "N/A");
            setfScore(jsonApp.data?.scores?.financial?.score ?? "N/A");
            setbScore(jsonApp.data?.scores?.bureau?.score ?? "N/A");
            setpScore(jsonApp.data?.scores?.application?.score ?? "N/A");
            
            // Get file progress display
            const fp = jsonApp.data?.fileProgress?.display;
            if (typeof fp === 'string') setDocProgress(fp);
            
            // Get verification progress data
            const verificationData = jsonApp.data?.verificationProgress;
            if (typeof verificationData === 'string') {
              setVerProgress(verificationData);  // Use backend value as-is
            } else {
              setVerProgress(`0/21`);  // Default value
            }
            
            // Get totalFiles from application details
            const total = jsonApp.data?.totalJSONFiles || 21;
            setTotalFiles(total);
          }
        }
      } catch (err) {
        console.error("Score fetch error:", err);
      }
    };

    fetchScores();
  }, [applicationId, totalFiles]);

  return (
    <Box>
      {/* Top Row - Two Cards */}
      <Stack spacing={2} alignItems="stretch" justifyContent="center" sx={{ height: "100%", width: "100%" }}>
        <Card
          sx={{
          borderRadius: "1rem",
            boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
            width: "100%",
        p: { xs: "1rem", sm: "1.25rem" },
px: { xs: "1.25rem", sm: "1.75rem" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            transition: "color 0.4s ease-in-out",
            zIndex: 0,
            cursor: "pointer",
            "&:hover": {
              color: "white",
              "& .outer-text": {
                color: "white !important",
                transition: "color 0.4s ease-in-out",
              },
              "& svg": {
                color: "white",
                transition: "color 0.4s ease-in-out",
              },
              "& .progress-bar-bg": {
                backgroundColor: "#C7C7C7 !important",
              },
              "& .progress-bar-fill": {
                backgroundColor: "#5ABA85 !important",
              },
              "&::after": {
                transform: "scaleY(1)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#034A7F",
              zIndex: 0,
              transform: "scaleY(0)",
              transformOrigin: "bottom",
              transition: "transform 0.4s ease-in-out",
            },
            "&:hover .score-divider": {
              borderColor: "white !important",
            },
          }}
        >
          <Stack spacing={1}>
            <Box sx={{ width: "100%", mt: 2 }}>
              <Collapse in={true} timeout="auto" unmountOnExit>
              <Box>
                  <Box
                    sx={{
                     display: "flex",
flexDirection: { xs: "column", sm: "row" },
alignItems: "stretch",
justifyContent: "space-between",
width: "100%",
gap: "1rem",
                    }}
                  >
                    {[
                      { icon: applicationScoreIcon, title: "Application Score", value: overallScore },
                      { icon: fileUploadIcon, title: "Financial Score", value: fScore },
                      { icon: verificationIcon, title: "Bureau Score", value: bScore },
                      { icon: lowRiskIcon, title: "Profile Score", value: pScore },
                    ].map((item, index) => {
                      // Round value if it exists, else null
                      // const roundedValue = item.value != null ? Math.round(item.value * 10) / 10 : null;
                      //  const roundedValue = item.value != null ? Number(item.value.toFixed(1)) : null;
                      const roundedValue =
                              item.value != null && !isNaN(parseFloat(item.value))
                                ? Number(parseFloat(item.value).toFixed(1))
                                : null;
                      
                      const backgroundColor = roundedValue != null && roundedValue < 3.5 ? "#ff7769" : "#fff";
                      const textColor = roundedValue != null && roundedValue < 3.5 ? "#fff" : "#034A7F";
                      const valueColor = roundedValue != null && roundedValue < 3.5 ? "#fff" : "#C7C7C7";
                      const imageFilter =
                                          roundedValue != null && roundedValue < 3.5
                                            ? "brightness(0) invert(1)"   //  makes icon white
                                            : "none";

                  
                      // const topBorderColor = roundedValue != null
                      //                   ? roundedValue < 3.5
                      //                     ? "#ff7769"  // orange if <3.5
                      //                     : "#5aBA85"  // green if >=3.5
                      //                    :"#e0e0e0";
                      // console.log(`Top Border Color: ${topBorderColor}`);
 
                      return (
                        <React.Fragment key={index}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                          borderRadius: "0.75rem",
                             boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
                              // borderTop: `6px solid ${topBorderColor}`, //  dynamic top border
  
                              // borderTopLeftRadius: "12px",  //  Ensure top corners are rounded
                              // borderTopRightRadius: "12px", //  Ensure top corners are rounded
                            p: { xs: "1rem", sm: "0.8rem" },
                            minHeight: { xs: "4rem", sm: "auto" },
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              backgroundColor: backgroundColor, // always white
                              flex: 1,
                              position: "relative",
                              zIndex: 2,
                              transition: "all 0.4s ease-in-out",
                              
                            }}
                          >
                            <img src={item.icon} alt={item.title} style={{ width: "2.2rem", height: "2.2rem", filter: imageFilter,  }} />
                            <Box>
                              <Typography sx={{ color: textColor , fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                                {item.title}
                              </Typography>
                              <Typography sx={{ color: valueColor, fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                                {roundedValue != null ? roundedValue : "N/A"}
                              </Typography>
                            </Box>
                          </Box>
 
                          {index === 0 && (
                            <Divider
                              className="score-divider"
                              orientation="vertical"
                              flexItem
                              sx={{
                                mx: "0.5rem",
                                borderColor: "rgba(150,150,150,0.4)",
                                zIndex: 5,
                                position: "relative",
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
 
              </Collapse>
            </Box>
          </Stack>
        </Card>
      </Stack>

      {/* Bottom Row - Two Cards Side by Side */}
      <Stack
      direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mt={2}
        alignItems="stretch"
      >
        {/* Documents Card */}
        <Card
         sx={{
        borderRadius: "1rem",
       p: { xs: "1rem", sm: "1.25rem" },
boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
        width: { xs: "100%", sm: "50%" },
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .outer-text": {
            color: "white !important",
            transition: "color 0.4s ease-in-out",
          },
          "& svg": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .progress-bar-bg": {
            backgroundColor: "#C7C7C7 !important",
          },
          "& .progress-bar-fill": {
            backgroundColor: "#5ABA85 !important",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: 0,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
        "&:hover .score-divider": {
          borderColor: "white !important",
        },
      }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack spacing={1}>
              {/* Header with Progress */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ width: "100%" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="outer-text"
                  sx={{ color: "#034A7F" }}
                >
                  Onboarding Status
                </Typography>
                <Box sx={{ minWidth: { xs: "100%", sm: "12.5rem" } }}>
                  <ProgressBadge percent={50} />
                </Box>
              </Stack>

              <Box sx={{ width: "100%", mt: 2 }}>
                <Collapse in={true} timeout="auto" unmountOnExit>
                  <Box>
                    <Box
                      sx={{
                         display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "stretch",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "1rem",
                      }}
                    >
                      {[
                        { icon: fileUploadIcon, title: "Documents", value: docProgress || "0/0" },
                        { 
                          icon: verificationIcon, 
                          title: `Verification`,
                          value: verProgress || "0/0" 
                        },
                      ].map((item, index) => (
                        <React.Fragment key={index}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                             borderRadius: "0.75rem",
                             boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
                           p: { xs: "1rem", sm: "0.8rem" },
minHeight: { xs: "4rem", sm: "auto" },
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              backgroundColor: "#fff",
                              flex: 1,
                              position: "relative",
                              zIndex: 2,
                              transition: "all 0.4s ease-in-out",
                              "&:hover": {
                                backgroundColor: "#fff",
                                borderColor: "#e0e0e0",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                                transform: "none",
                              },
                            }}
                          >
                            <img
                              src={item.icon}
                              alt={item.title}
                              style={{ width: 35, height: 35 }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  color: "#034A7F",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#C7C7C7",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                }}
                              >
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </Stack>
          </Box>
        </Card>

        {/* Application Summary Card */}
        <Card
          sx={{
          borderRadius: "1rem",
           p: { xs: "1rem", sm: "1.25rem" },
         boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
          width: { xs: "100%", sm: "50%" }, 
            position: "relative",
            overflow: "hidden",
            transition: "color 0.4s ease-in-out",
            zIndex: 0,
            cursor: "pointer",
            "&:hover": {
              color: "white",
              "& .MuiTypography-root": {
                color: "white",
                transition: "color 0.4s ease-in-out",
              },
              "& .hoverText": {
                color: "#034A7F",
              },
              "&::after": {
                transform: "scaleY(1)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#034A7F",
              zIndex: -1,
              transform: "scaleY(0)",
              transformOrigin: "bottom",
              transition: "transform 0.4s ease-in-out",
            },
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            mb={1}
            sx={{ color: "#034A7F" }}
          >
           Overall Summary
          </Typography>

          <Box
            sx={{
              border: "1px solid #C7C7C7",
           borderRadius: "0.5rem",
          p: "1rem",
              backgroundColor: "#fff",
            minHeight: { xs: "6rem", sm: "7.5rem" },
            }}
          >
            <Typography
              variant="body2"
              className="hoverText"
              sx={{
                color: "#C7C7C7",
                transition: "color 0.4s ease-in-out",
              }}
            >
           Coming Soon...
            </Typography>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
};

// Tab 4: Credit Memo Content
const CreditMemoTab = ({ isSmallScreen, verificationCount = '0/0' }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const applicationId = queryParams.get("id");
  const [docProgress, setDocProgress] = useState("0/0");
  const [verProgress, setVerProgress] = useState("0/0");
  const [totalFiles, setTotalFiles] = useState(0);
  
  // Add score states
  const [applicationScore, setApplicationScore] = useState("N/A");
  const [financialScore, setFinancialScore] = useState("N/A");
  const [bureauScore, setBureauScore] = useState("N/A");
  const [profileScore, setProfileScore] = useState("N/A");
  const [memoNo, setMemoNo] = useState("N/A");
  const [registrationDate, setRegistrationDate] = useState("N/A");

  // Calculate verification percentage
  const getVerificationPercent = () => {
    try {
      const [verified, total] = verProgress.split('/').map(Number);
      if (total > 0) {
        return Math.round((verified / total) * 100);
      }
    } catch (e) {}
    return 0;
  };

  useEffect(() => {
    if (!applicationId) {
      console.log(' [CREDIT MEMO] No applicationId provided');
      return;
    }
    
    const fetchScores = async () => {
      try {
        console.log(' [CREDIT MEMO] Fetching scores for applicationId:', applicationId);
        
        // OPTIMIZED: Fetch application details directly instead of entire list
        const resApp = await fetch(`${API_BASE}/applications/${applicationId}/details`);
        if (resApp.ok) {
          const jsonApp = await resApp.json();
          console.log(' [CREDIT MEMO] Application details response:', jsonApp);
          console.log(' [CREDIT MEMO] Application data keys:', jsonApp.data ? Object.keys(jsonApp.data) : 'no data');
          
          // Safe logging to prevent object rendering errors
          const proprietorshipData = jsonApp.data?.proprietorship;
          if (proprietorshipData && typeof proprietorshipData === 'object') {
            console.log(' [CREDIT MEMO] Proprietorship object found with keys:', Object.keys(proprietorshipData));
          } else {
            console.log(' [CREDIT MEMO] No proprietorship object found');
          }
          
          if (jsonApp.success && jsonApp.data) {
            // Get totalFiles from application details
            const total = jsonApp.data?.totalJSONFiles || 21;
            const safeTotal = typeof total === 'number' ? total : (parseInt(total) || 21);
            setTotalFiles(safeTotal);
            console.log(' [CREDIT MEMO] Total files set to:', safeTotal, 'type:', typeof safeTotal);
            
            // Set file progress
            const fp = jsonApp.data?.fileProgress?.display;
            if (typeof fp === 'string') {
              setDocProgress(fp);
            } else {
              setDocProgress("0/0");
            }
            
            // Set verification progress
            const verificationData = jsonApp.data?.verificationProgress;
            if (typeof verificationData === 'string') {
              setVerProgress(verificationData);
              console.log(' [CREDIT MEMO] Verification progress:', verificationData);
            } else {
              setVerProgress(`0/21`);
              console.log(' [CREDIT MEMO] Using default verification progress: 0/21');
            }
            
            // Set memo number and registration date - ensure they are strings
            const memoValue = jsonApp.data?.memoNo || applicationId || "N/A";
            const dateValue = jsonApp.data?.registrationDate || jsonApp.data?.formattedDate || "N/A";
            
            console.log(' [CREDIT MEMO] Raw memo and date values:', {
              memoValue,
              dateValue,
              memoType: typeof memoValue,
              dateType: typeof dateValue,
              memoIsObject: typeof memoValue === 'object',
              dateIsObject: typeof dateValue === 'object',
              memoKeys: typeof memoValue === 'object' ? Object.keys(memoValue) : 'not object',
              dateKeys: typeof dateValue === 'object' ? Object.keys(dateValue) : 'not object'
            });
            
            // Ensure values are strings, not objects
            const safeMemoValue = typeof memoValue === 'object' ? JSON.stringify(memoValue) : String(memoValue);
            const safeDateValue = typeof dateValue === 'object' ? JSON.stringify(dateValue) : String(dateValue);
            
            setMemoNo(safeMemoValue);
            setRegistrationDate(safeDateValue);
            
            // Set scores from the application details endpoint
            if (jsonApp.data?.scores) {
              const scores = jsonApp.data.scores;
              
              // Use the scores directly from the application details with proper fallbacks and type checking
              const appScore = scores.overall?.score;
              const finScore = scores.financial?.score;
              const burScore = scores.bureau?.score;
              const profScore = scores.application?.score;
              
              // Ensure all values are strings or numbers, not objects
              const safeAppScore = (appScore !== null && appScore !== undefined && typeof appScore !== 'object') 
                ? appScore 
                : "N/A";
              const safeFinScore = (finScore !== null && finScore !== undefined && typeof finScore !== 'object') 
                ? finScore 
                : "N/A";
              const safeBurScore = (burScore !== null && burScore !== undefined && typeof burScore !== 'object') 
                ? burScore 
                : "N/A";
              const safeProfScore = (profScore !== null && profScore !== undefined && typeof profScore !== 'object') 
                ? profScore 
                : "N/A";
              
              setApplicationScore(safeAppScore);
              setFinancialScore(safeFinScore);
              setBureauScore(safeBurScore);
              setProfileScore(safeProfScore);
              
              console.log(' [CREDIT MEMO] Scores set from application details:', {
                application: safeAppScore,
                financial: safeFinScore,
                bureau: safeBurScore,
                profile: safeProfScore,
                types: {
                  app: typeof safeAppScore,
                  fin: typeof safeFinScore,
                  bur: typeof safeBurScore,
                  prof: typeof safeProfScore
                }
              });
            } else {
              console.log(' [CREDIT MEMO] No scores found in application details');
            }
          } else {
            console.log(' [CREDIT MEMO] Invalid application details response structure');
          }
        } else {
          console.log(' [CREDIT MEMO] Application details failed');
        }
        
        console.log(' [CREDIT MEMO] All score fetching completed');
        
      } catch (error) {
        console.error(' [CREDIT MEMO] Error fetching scores:', error);
      }
    };
    
    fetchScores();
  }, [applicationId]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Top Row - Two Cards */}
      <Stack spacing={2} alignItems="stretch" justifyContent="center" sx={{ height: "100%", width: "100%" }}>
        <Card
          sx={{
           borderRadius: "1rem",
boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
            width: "100%",
          p: { xs: "1rem", sm: "1.25rem" },
px: { xs: "1.25rem", sm: "1.75rem" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            transition: "color 0.4s ease-in-out",
            zIndex: 0,
            cursor: "pointer",
            "&:hover": {
              color: "white",
              "& .outer-text": {
                color: "white !important",
                transition: "color 0.4s ease-in-out",
              },
              "& svg": {
                color: "white",
                transition: "color 0.4s ease-in-out",
              },
              "& .progress-bar-bg": {
                backgroundColor: "#C7C7C7 !important",
              },
              "& .progress-bar-fill": {
                backgroundColor: "#5ABA85 !important",
              },
              "&::after": {
                transform: "scaleY(1)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#034A7F",
              zIndex: 0,
              transform: "scaleY(0)",
              transformOrigin: "bottom",
              transition: "transform 0.4s ease-in-out",
            },
          }}
        >
          <Stack spacing={1}>
            <Box sx={{ width: "100%", mt: 2 }}>
              <Collapse in={true} timeout="auto" unmountOnExit>
                <Box>
                  <Box
                    sx={{
                    display: "flex",
flexDirection: { xs: "column", sm: "row" },
alignItems: "stretch",
justifyContent: "space-between",
width: "100%",
gap: "1rem",
                    }}
                  >
                    {[
                      { icon: applicationScoreIcon, title: "Application Score", value: typeof applicationScore === 'object' ? 'N/A' : (applicationScore || 'N/A') },
                      { icon: fileUploadIcon, title: "Financial Score", value: typeof financialScore === 'object' ? 'N/A' : (financialScore || 'N/A') },
                      { icon: verificationIcon, title: "Bureau Score", value: typeof bureauScore === 'object' ? 'N/A' : (bureauScore || 'N/A') },
                      { icon: lowRiskIcon, title: "Profile Score", value: typeof profileScore === 'object' ? 'N/A' : (profileScore || 'N/A') },
                    ].map((item, index) => (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            border: "1px solid #e0e0e0",
                          borderRadius: "0.75rem",
boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
p: { xs: "1rem", sm: "0.8rem" },
minHeight: { xs: "4rem", sm: "auto" },
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            backgroundColor: "#fff",
                            flex: 1,
                            position: "relative",
                            zIndex: 2,
                            transition: "all 0.4s ease-in-out",
                            "&:hover": {
                              backgroundColor: "#fff",
                              borderColor: "#e0e0e0",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                              transform: "none",
                            },
                          }}
                        >
                          <img
                            src={item.icon}
                            alt={item.title}
                           style={{ width: "2.2rem", height: "2.2rem" }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                color: "#034A7F",
                                fontWeight: 600,
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{
                                color: "#C7C7C7",
                                fontWeight: 600,
                                fontSize: "1rem",
                              }}
                            >
                              {typeof item.value === 'object' ? 'N/A' : (item.value || 'N/A')}
                            </Typography>
                          </Box>
                        </Box>

                        {index === 0 && (
                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                              mx: "0.5rem",
                              borderColor: "rgba(0,0,0,0.1)",
                              alignSelf: "stretch",
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Stack>
        </Card>
      </Stack>

      {/* Bottom Row - Two Cards Side by Side */}
      <Stack
       direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mt={2}
        alignItems="stretch"
      >
        {/* Documents Card */}
        <Card
          sx={{
        borderRadius: "1rem",
       p: { xs: "1rem", sm: "1.25rem" },
boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
        width: { xs: "100%", sm: "50%" },
        position: "relative",
        overflow: "hidden",
        transition: "color 0.4s ease-in-out",
        zIndex: 0,
        cursor: "pointer",
        "&:hover": {
          color: "white",
          "& .outer-text": {
            color: "white !important",
            transition: "color 0.4s ease-in-out",
          },
          "& svg": {
            color: "white",
            transition: "color 0.4s ease-in-out",
          },
          "& .progress-bar-bg": {
            backgroundColor: "#C7C7C7 !important",
          },
          "& .progress-bar-fill": {
            backgroundColor: "#5ABA85 !important",
          },
          "&::after": {
            transform: "scaleY(1)",
          },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "#034A7F",
          zIndex: 0,
          transform: "scaleY(0)",
          transformOrigin: "bottom",
          transition: "transform 0.4s ease-in-out",
        },
        "&:hover .score-divider": {
          borderColor: "white !important",
        },
      }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack spacing={1}>
              {/* Header with Progress */}
              <Stack
                 direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            sx={{ width: "100%" }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="outer-text"
                  sx={{ color: "#034A7F" }}
                >
                  OnBoarding Status
                </Typography>
               <Box sx={{ minWidth: { xs: "100%", sm: "12.5rem" } }}>
                  <ProgressBadge percent={getVerificationPercent()} />
                </Box>
              </Stack>

              <Box sx={{ width: "100%", mt: 2 }}>
                <Collapse in={true} timeout="auto" unmountOnExit>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "1rem",
                      }}
                    >
                      {[
                        { icon: fileUploadIcon, title: "Documents", value: typeof docProgress === 'object' ? "0/0" : (docProgress || "0/0") },
                        { 
                          icon: verificationIcon, 
                          title: `Verification (${typeof totalFiles === 'number' ? totalFiles : 23} files)`,
                          value: typeof verProgress === 'object' ? "0/0" : (verProgress || "0/0") 
                        },
                      ].map((item, index) => (
                        <React.Fragment key={index}>
                          <Box
                            sx={{
                              border: "1px solid #e0e0e0",
                             borderRadius: "0.75rem",
boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.05)",
p: { xs: "1rem", sm: "0.8rem" },
minHeight: { xs: "4rem", sm: "auto" },
                              display: "flex",
                              alignItems: "center",
                              gap: "0.75rem",
                              backgroundColor: "#fff",
                              flex: 1,
                              position: "relative",
                              zIndex: 2,
                              transition: "all 0.4s ease-in-out",
                              "&:hover": {
                                backgroundColor: "#fff",
                                borderColor: "#e0e0e0",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                                transform: "none",
                              },
                            }}
                          >
                            <img
                              src={item.icon}
                              alt={item.title}
                              style={{ width: 35, height: 35 }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  color: "#034A7F",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#C7C7C7",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                }}
                              >
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            </Stack>
          </Box>
        </Card>

        {/* Application Summary Card */}
        <Card
          sx={{
          borderRadius: "1rem",
p: { xs: "1rem", sm: "1.25rem" },
boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.08)",
width: { xs: "100%", sm: "50%" },
            position: "relative",
            overflow: "hidden",
            transition: "color 0.4s ease-in-out",
            zIndex: 0,
            cursor: "pointer",
            "&:hover": {
              color: "white",
              "& .MuiTypography-root": {
                color: "white",
                transition: "color 0.4s ease-in-out",
              },
              "& .hoverText": {
                color: "#034A7F",
              },
              "&::after": {
                transform: "scaleY(1)",
              },
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "#034A7F",
              zIndex: -1,
              transform: "scaleY(0)",
              transformOrigin: "bottom",
              transition: "transform 0.4s ease-in-out",
            },
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            mb={1}
            sx={{ color: "#034A7F" }}
          >
            Profile Summary
          </Typography>

          <Box
            sx={{
              border: "1px solid #C7C7C7",
             borderRadius: "0.5rem",
p: "1rem",
minHeight: { xs: "6rem", sm: "7.5rem" },
              backgroundColor: "#fff",
            
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#034A7F",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  Memo No:
                </Typography>
                <Typography
                  variant="body2"
                  className="hoverText"
                  sx={{
                    color: "#C7C7C7",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    transition: "color 0.4s ease-in-out",
                  }}
                >
                  {typeof memoNo === 'object' ? 'N/A' : (memoNo || 'N/A')}
                </Typography>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#034A7F",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  Registration Date:
                </Typography>
                <Typography
                  variant="body2"
                  className="hoverText"
                  sx={{
                    color: "#C7C7C7",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    transition: "color 0.4s ease-in-out",
                  }}
                >
                  {typeof registrationDate === 'object' ? 'N/A' : (registrationDate || 'N/A')}
                </Typography>
              </Box>
              
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#034A7F",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  Application ID:
                </Typography>
                <Typography
                  variant="body2"
                  className="hoverText"
                  sx={{
                    color: "#C7C7C7",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    transition: "color 0.4s ease-in-out",
                  }}
                >
                  {typeof applicationId === 'object' ? 'N/A' : (applicationId || 'N/A')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
};

export default function InfoCards({ activeTab = 0, verificationCount = '0/0' }) {
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const [expanded, setExpanded] = useState("panel0");

  // Conditional rendering based on activeTab
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Application Details
        return <ApplicationDetailsTab isSmallScreen={isSmallScreen} verificationCount={verificationCount} />;
      case 1: // Financial Details
        return <FinancialDetailsTab isSmallScreen={isSmallScreen} verificationCount={verificationCount} />;
      case 2: // Bureau Details
        return <BureauDetailsTab isSmallScreen={isSmallScreen} verificationCount={verificationCount} />;
      case 3: // Scorecard Details
        return <ScorecardDetailsTab isSmallScreen={isSmallScreen} verificationCount={verificationCount} />;
      case 4: // Credit Memo
        return <CreditMemoTab isSmallScreen={isSmallScreen} verificationCount={verificationCount} />;
      default:
        return <ApplicationDetailsTab isSmallScreen={isSmallScreen} verificationCount={verificationCount} />;
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      {renderTabContent()}
    </Box>
  );
}